'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OtherIncome, OtherIncomeInput } from '@garage/shared'
import {
  otherIncomeInputSchema,
  OTHER_INCOME_KINDS,
  OTHER_INCOME_KIND_LABELS,
  parseMoneyToMinor,
} from '@garage/shared'
import { Dialog, TextField, Select, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface OtherIncomeFormDialogProps {
  open: boolean
  income?: OtherIncome
  onClose: () => void
}

interface FormState {
  kind: 'parts_sale' | 'other'
  description: string
  date: string
  amountBeforeVatStr: string
  vatStr: string
  paymentMethod: 'cash' | 'card' | 'bank' | 'other'
  notes: string | null
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function OtherIncomeFormDialog({ open, income, onClose }: OtherIncomeFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !income

  const initialState = (): FormState => ({
    kind: income?.kind ?? 'parts_sale',
    description: income?.description ?? '',
    date: income?.date ?? today(),
    amountBeforeVatStr: income ? String(income.amountBeforeVatMinor / 100) : '',
    vatStr: income ? String(income.vatMinor / 100) : '',
    paymentMethod: income?.paymentMethod ?? 'cash',
    notes: income?.notes ?? null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, income])

  const mutation = useMutation({
    mutationFn: async (input: OtherIncomeInput) =>
      isCreate ? api.otherIncome.create(input) : api.otherIncome.update(income!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.otherIncome({}) })
      queryClient.invalidateQueries({ queryKey: ['finance-advanced'] })
      snackbar.success(isCreate ? t('accounting.advanced.incomeAdded') : t('accounting.advanced.incomeUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: OtherIncomeInput = {
      kind: formState.kind,
      description: formState.description,
      date: formState.date,
      amountBeforeVatMinor: parseMoneyToMinor(formState.amountBeforeVatStr) ?? 0,
      vatMinor: parseMoneyToMinor(formState.vatStr) ?? 0,
      paymentMethod: formState.paymentMethod,
      notes: formState.notes || null,
    }

    const result = otherIncomeInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  const kindOptions = OTHER_INCOME_KINDS.map((k) => ({
    value: k,
    label: OTHER_INCOME_KIND_LABELS[k],
  }))

  const paymentMethodOptions = [
    { value: 'cash', label: t('accounting.advanced.paymentMethods.cash') },
    { value: 'card', label: t('accounting.advanced.paymentMethods.card') },
    { value: 'bank', label: t('accounting.advanced.paymentMethods.bank') },
    { value: 'other', label: t('accounting.advanced.paymentMethods.other') },
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('accounting.advanced.addIncome') : t('accounting.advanced.editIncome')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label={t('accounting.advanced.kind')}
          options={kindOptions}
          value={formState.kind}
          onChange={(value) => setFormState((s) => ({ ...s, kind: value as 'parts_sale' | 'other' }))}
          error={fieldErrors.kind}
          fullWidth
        />

        <TextField
          label={t('accounting.advanced.description')}
          value={formState.description}
          onChange={(v) => setFormState((s) => ({ ...s, description: v }))}
          error={fieldErrors.description}
          fullWidth
          autoFocus
        />

        <TextField
          label={t('calendar.date')}
          type="date"
          value={formState.date}
          onChange={(v) => setFormState((s) => ({ ...s, date: v }))}
          error={fieldErrors.date}
          fullWidth
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('accounting.beforeVat')}
            value={formState.amountBeforeVatStr}
            onChange={(v) => setFormState((s) => ({ ...s, amountBeforeVatStr: v }))}
            error={fieldErrors.amountBeforeVatMinor}
            numeric
            fullWidth
          />
          <TextField
            label={t('accounting.vat')}
            value={formState.vatStr}
            onChange={(v) => setFormState((s) => ({ ...s, vatStr: v }))}
            error={fieldErrors.vatMinor}
            numeric
            fullWidth
          />
        </div>

        <Select
          label={t('accounting.advanced.paymentMethod')}
          options={paymentMethodOptions}
          value={formState.paymentMethod}
          onChange={(value) => setFormState((s) => ({ ...s, paymentMethod: value as 'cash' | 'card' | 'bank' | 'other' }))}
          error={fieldErrors.paymentMethod}
          fullWidth
        />

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={3}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
